import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../i18n';
import { VideoCard } from '../components/VideoCard';
import { Seo } from '../components/Seo';
import { translateCategory } from '../lib/categories';
import { isSafeMode } from '../lib/safeMode';

export default function CategoriesPage() {
    const { name } = useParams();
    const { t, locale } = useI18n();
    const { user } = useAuth();
    const [cats, setCats] = useState([]);
    const [adultCats, setAdultCats] = useState([]);
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [videos, setVideos] = useState([]);
    const active = name || null;
    const canViewAdult = !isSafeMode()
        && Number(user?.age || 0) >= 18
        && user?.adult_content_enabled
        && (user?.subscription_active || user?.role === 'admin');
    const activeIsAdult = adultCats.includes(active);

    useEffect(() => {
        (async () => {
            const r = await api.get('/categories');
            setCats(r.data.categories || []);
            setAdultCats(r.data.adult_categories || []);
            setCategoriesLoaded(true);
        })();
    }, []);

    useEffect(() => {
        (async () => {
            if (!categoriesLoaded) return;
            if (activeIsAdult && !canViewAdult) {
                setVideos([]);
                return;
            }
            const r = await api.get('/videos', { params: {
                category: active && !activeIsAdult ? active : undefined,
                adult_category: activeIsAdult ? active : undefined,
                limit: 60,
            } });
            setVideos(r.data.items || []);
        })();
    }, [active, activeIsAdult, canViewAdult, categoriesLoaded]);

    return (
        <main className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 py-6 md:py-10 pb-24 md:pb-12" data-testid="categories-page">
            <Seo
                path={active ? `/categories/${encodeURIComponent(active)}` : '/categories'}
                title={active ? `${translateCategory(active, locale)} — Vídeos` : 'Categories de vídeos'}
                description={active ? `Mira els millors vídeos de ${translateCategory(active, locale)} a Albambam, la plataforma de vídeo en català.` : 'Explora els vídeos d’Albambam per categories: cinema, música, esports, videojocs, comèdia i molt més.'}
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        { '@type': 'ListItem', position: 1, name: 'Inici', item: 'https://albambam.com/' },
                        { '@type': 'ListItem', position: 2, name: 'Categories', item: 'https://albambam.com/categories' },
                        ...(active ? [{ '@type': 'ListItem', position: 3, name: translateCategory(active, locale) }] : []),
                    ],
                }}
            />
            <h1 className="font-display text-2xl md:text-3xl font-semibold mb-4">{t('cats.title')}</h1>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="category-chips">
                <Link to="/categories" className={`px-3 py-1.5 rounded-full text-sm border ${!active ? 'bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] border-[color:var(--alb-sun)]' : 'border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface)]'}`}>{t('cats.all')}</Link>
                {cats.map((c) => (
                    <Link key={c} to={`/categories/${encodeURIComponent(c)}`} className={`px-3 py-1.5 rounded-full text-sm border ${active === c ? 'bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] border-[color:var(--alb-sun)]' : 'border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface)]'}`}>{translateCategory(c, locale)}</Link>
                ))}
            </div>
            {canViewAdult && (
                <section className="mb-6" aria-labelledby="adult-categories-title">
                    <h2 id="adult-categories-title" className="font-display text-lg font-semibold mb-2">{t('cats.adult_title')}</h2>
                    <div className="flex flex-wrap gap-2" data-testid="adult-category-chips">
                        {adultCats.map((category) => (
                            <Link key={category} to={`/categories/${encodeURIComponent(category)}`} className={`px-3 py-1.5 rounded-full text-sm border ${active === category ? 'bg-[color:var(--alb-tangerine)] text-[color:var(--alb-ink)] border-[color:var(--alb-tangerine)]' : 'border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface)]'}`}>{translateCategory(category, locale)}</Link>
                        ))}
                    </div>
                </section>
            )}
            {videos.length === 0 ? (
                <div className="text-[color:var(--alb-text-muted)] text-sm">{t('cats.empty')}</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {videos.map((v) => <VideoCard key={v.video_id} video={v} />)}
                </div>
            )}
        </main>
    );
}
